import { MutableRefObject, useEffect, useState } from 'react';
import noop from 'lodash/noop';

export type BasicTarget<T = HTMLElement> =
  | (() => T | null)
  | T
  | null
  | MutableRefObject<T | null | undefined>;

type TargetElement = HTMLElement | Element | Document | Window;

export function getTargetElement(
  target?: BasicTarget<TargetElement>,
  defaultElement?: TargetElement,
): TargetElement | undefined | null {
  if (!target) {
    return defaultElement;
  }

  let targetElement: TargetElement | undefined | null;

  if (typeof target === 'function') {
    targetElement = target();
  } else if ('current' in target) {
    targetElement = target.current;
  } else {
    targetElement = target;
  }

  return targetElement;
}

type IsInViewport = boolean | undefined;

function getIsInViewport(el: HTMLElement, initialValue: boolean): boolean {
  if (!el) {
    return initialValue;
  }

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  const rect = el.getBoundingClientRect();

  if (rect) {
    const {
      top, bottom, left, right,
    } = rect;
    return bottom > 0 && top <= viewportHeight && left <= viewportWidth && right > 0;
  }

  return false;
}

function useIsInViewport(target: BasicTarget, initialValue = false): IsInViewport {
  const [isInViewport, setIsInViewport] = useState<IsInViewport>(() => {
    const el = getTargetElement(target);

    return getIsInViewport(el as HTMLElement, initialValue);
  });

  useEffect(() => {
    const el = getTargetElement(target);
    if (!el) {
      return noop;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => setIsInViewport(entry.isIntersecting));
    });

    observer.observe(el as HTMLElement);

    return () => {
      observer.disconnect();
    };
  }, [target]);

  return isInViewport;
}

export default useIsInViewport;
